import { useEffect, useState, useCallback } from 'react'
import { apiGet } from '../api'

interface ActivityEvent {
  id: number
  ts: number
  kind: string
  source: string | null
  scope: string | null
  title: string
  detail: string | null
}

const KIND_TINT: Record<string, string> = {
  status_flip:    '#ffb454',
  issue_created:  '#7dd3fc',
  mercedes_send:  '#3ddc84',
  auto_stop:      '#ff9e6d',
  manual_stop:    '#ff6b81',
  manual_start:   '#3ddc84',
  provider_sync:  '#9aa6c2',
  cost_snapshot:  '#9aa6c2',
  cert_warn:      '#ff6b81',
  ai_run:         '#c5b3ff',
  session_start:  '#5af2ff',
}

function fmt(ts: number): string {
  const d = new Date(ts)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function ActivityTimeline() {
  const [data, setData] = useState<ActivityEvent[] | null>(null)
  const [hours, setHours] = useState(24)

  const load = useCallback(async () => {
    try {
      const r = await apiGet<{ events: ActivityEvent[] }>(`/activity?hours=${hours}`)
      setData(r.events)
    } catch { /* ignore */ }
  }, [hours])

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [load])

  return (
    <section className="panel work-panel">
      <div className="panel-header">
        <span>Activity · last {hours}h</span>
        <span style={{ display: 'flex', gap: 4 }}>
          {[6, 24, 72, 168].map(h => (
            <button key={h} type="button" onClick={() => setHours(h)}
              style={{ minHeight: 24, padding: '0 8px', fontSize: '0.65rem',
                       background: hours === h ? 'rgba(90,242,255,0.10)' : 'transparent',
                       borderColor: hours === h ? 'rgba(90,242,255,0.45)' : 'var(--border-soft)',
                       color: hours === h ? 'var(--accent)' : 'var(--text-secondary)' }}>
              {h < 168 ? `${h}h` : '7d'}
            </button>
          ))}
        </span>
      </div>
      {!data && <div className="empty-state">Loading…</div>}
      {data && data.length === 0 && <div className="empty-state">Nothing yet — events will land here as they happen.</div>}
      {data && data.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflowY: 'auto' }}>
          {data.map(ev => {
            const tint = KIND_TINT[ev.kind] || '#9aa6c2'
            return (
              <div key={ev.id} style={{
                display: 'flex', alignItems: 'baseline', gap: 8,
                padding: '5px 6px', borderRadius: 6,
                fontSize: '0.78rem',
              }} title={ev.detail ?? ''}>
                <span className="mono" style={{ color: 'var(--text-muted)', fontSize: '0.65rem',
                                                 flex: '0 0 60px' }}>{fmt(ev.ts)}</span>
                <span style={{ flex: '0 0 6px', width: 6, height: 6, borderRadius: '50%',
                               background: tint, alignSelf: 'center' }} />
                <span style={{ flex: '0 0 90px', fontFamily: 'var(--font-mono)',
                               fontSize: '0.6rem', letterSpacing: '0.06em',
                               textTransform: 'uppercase', color: tint }}>
                  {ev.kind}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                               textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.title}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
